/**
 * Replies API - Retry Helper
 *
 * Internal retry helper for transient Firestore errors.
 */

/**
 * Runs `fn` up to `maxRetries` times, applying exponential back-off with jitter
 * between attempts.  Only retries on transient Firestore errors (unavailable,
 * deadline-exceeded, network-related).
 *
 * Kept local to repliesApi so it has no runtime dependency on the legacy
 * userOperations.js retryOperation function.
 *
 * @template T
 * @param {() => Promise<T>} fn
 * @param {{ maxRetries?: number, baseDelay?: number }} [opts]
 * @returns {Promise<T>}
 */
export const retryOp = async (fn, { maxRetries = 3, baseDelay = 1000 } = {}) => {
  let lastErr;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (e) {
      lastErr = e;
      const retryable =
        e.code === "unavailable" ||
        e.code === "deadline-exceeded" ||
        String(e.message).includes("network");
      if (!retryable || attempt === maxRetries) throw e;
      const jitter = 1 + (Math.random() * 0.4 - 0.2);
      const delay = baseDelay * Math.pow(2, attempt - 1) * jitter;
      await new Promise((r) => setTimeout(r, delay));
    }
  }
  throw lastErr;
};
